<script setup>
/* add class .vss-movable to <v-card-title> to make a dialog movable */
const props = defineProps({
  headerElementSelector: { type: String, default: null }, // Selector for the draggable header
  movableElementSelector: { type: String, default: null }, // Selector for the movable element
})

const emit = defineEmits(['onStartMove'])

const rootRef = ref(null) // Reference to the root DOM element of this component

const updateMovableClass = () => {
  if (props.headerElementSelector) {
    const headerElement = document.querySelector(props.headerElementSelector)
    if (headerElement && !headerElement.classList.contains('vss-movable')) {
      // Only add the class if it's not already present
      headerElement.classList.add('vss-movable')
    }
  }
}

onMounted(() => {
  (function () {
    // Add the vss-movable class initially when mounted
    updateMovableClass()

    const d = {}

    const isMovable = (targ) => targ?.classList?.contains("vss-movable")

    document.addEventListener("mousedown", (e) => {
      const isMovableElementSelected = props.headerElementSelector && props.movableElementSelector

      let movableElement
      let headerElement

      if (isMovableElementSelected) {
        // Use provided props to select elements
        movableElement = document.querySelector(props.movableElementSelector)
        headerElement = document.querySelector(props.headerElementSelector)
      } else {
        // Default behavior: Find movable dialog and header
        movableElement = e.target.closest(".v-overlay__content")
        headerElement = movableElement?.querySelector(".v-card-title")
      }

      if (
        e.button === 0 &&
        !!movableElement &&
        !!headerElement &&
        (isMovable(e.target) || isMovable(e.target.parentNode))
      ) {
        const rect = movableElement.getBoundingClientRect()

        if (isMovableElementSelected) {
          // Props case: Adjust for `absolute` positioning relative to `offsetParent`
          const offsetParent = movableElement.offsetParent
          const parentRect = offsetParent?.getBoundingClientRect() || { left: 0, top: 0 }

          d.elStartX = rect.left - parentRect.left
          d.elStartY = rect.top - parentRect.top
        } else {
          // Default case: Use `fixed` positioning relative to the viewport
          d.elStartX = rect.left
          d.elStartY = rect.top
        }

        d.el = movableElement
        d.handle = headerElement
        d.mouseStartX = e.clientX
        d.mouseStartY = e.clientY
        d.el.style.position = isMovableElementSelected ? "absolute" : "fixed"
        d.el.style.margin = 0
        d.oldTransition = d.el.style.transition
        d.el.style.transition = "none"

        emit("onStartMove")
      }
    })

    document.addEventListener("mousemove", (e) => {
      if (!d.el || !d.handle) return

      const isMovableElementSelected = props.headerElementSelector && props.movableElementSelector

      let newLeft, newTop

      if (isMovableElementSelected) {
        // Props case: Adjust for `absolute` positioning relative to `offsetParent`
        const offsetParent = d.el.offsetParent
        const parentRect = offsetParent?.getBoundingClientRect() || { left: 0, top: 0 }

        newLeft = e.clientX - d.mouseStartX + d.elStartX
        newTop = e.clientY - d.mouseStartY + d.elStartY

        d.el.style.left = Math.min(
          Math.max(newLeft, 0),
          parentRect.width - d.el.offsetWidth
        ) + "px"

        d.el.style.top = Math.min(
          Math.max(newTop, 0),
          parentRect.height - d.el.offsetHeight
        ) + "px"
      } else {
        // Default case: Use `fixed` positioning relative to the viewport
        d.el.style.left = Math.min(
          Math.max(d.elStartX + e.clientX - d.mouseStartX, 0),
          window.innerWidth - d.el.getBoundingClientRect().width
        ) + "px"
        d.el.style.top = Math.min(
          Math.max(d.elStartY + e.clientY - d.mouseStartY, 0),
          window.innerHeight - d.handle.getBoundingClientRect().height
        ) + "px"
      }
    })

    document.addEventListener("mouseup", () => {
      if (!d.el) return
      d.el.style.transition = d.oldTransition
      d.el = undefined
    })
  })()
})
</script>

<template>
  <!-- Bind rootRef to the root element of the component -->
  <div ref="rootRef" />
</template>

<style>
.vss-movable {
  cursor: grab;
}
.vss-movable:hover {
  background-color: #eee;
}
.vss-movable:active {
  cursor: grabbing;
}
</style>
